import React, { FC, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
    AddLeadConnectIntegrationMutationVariables,
    IntegrationType,
    useAddLeadConnectAddOnMutation,
    useAddLeadConnectIntegrationMutation,
    useDisconnectIntegrationMutation,
    useLeadConnectStatusQuery,
    useLeadConnectTestCallMutation,
    useRemoveLeadConnectAddOnMutation,
} from "generated/graphql";
import { addNotification } from "layouts/AppLayout/modules";
import { wrapUrqlMutation } from "utils/graphql";

import { LeadConnectDialog } from "../components/LeadConnectDialog";
import {
    defaultLeadConnectForm,
    getLeadConnectCardState,
    leadConnectFormFromSettings,
    leadConnectTestCallMessage,
} from "../components/leadConnectForm";
import { LEAD_CONNECT_NAME } from "../integrationCatalog";

export interface LeadConnectDialogContainerProps {
    appId: string;
    organizationId: string;
    active: boolean;
    onClose(): void;
    /** The integration or the add-on changed: the integrations page should refetch. */
    onChanged?(): void;
    /** Open on the cancel question alone, as the card's remove does. */
    confirmCancelOnOpen?: boolean;
}

/**
 * The Lead Connect dialog's data. Mount it only while the dialog is open: its query reads the
 * number live from Lead Connect, and pausing a query on open state is what froze the nav drawer.
 *
 * The add-on and the number come from leadConnectStatus rather than integrationStatus, so an API
 * that lags those fields fails this dialog alone, not the whole integrations page.
 */
export const LeadConnectDialogContainer: FC<LeadConnectDialogContainerProps> = ({
    appId,
    organizationId,
    active,
    onClose,
    onChanged,
    confirmCancelOnOpen,
}) => {
    const dispatch = useDispatch();

    const [status, reexecuteStatus] = useLeadConnectStatusQuery({
        requestPolicy: "cache-and-network",
        variables: { appId },
    });

    const [purchaseState, purchaseMutation] = useAddLeadConnectAddOnMutation();
    const purchase = wrapUrqlMutation([purchaseState, purchaseMutation]);

    const [setupState, setupMutation] = useAddLeadConnectIntegrationMutation();
    const setup = wrapUrqlMutation([setupState, setupMutation], {
        // A fallback (a number from a nearby town) comes back as a warning; it rides on the toast.
        onSuccessMessage: (data) => {
            const warnings = data?.app?.update?.integration?.addLeadConnectIntegration?.warnings;
            return {
                send: true,
                message: warnings?.length ? `${LEAD_CONNECT_NAME} saved. ${warnings.join(" ")}` : `${LEAD_CONNECT_NAME} saved`,
            };
        },
    });

    const [testState, testMutation] = useLeadConnectTestCallMutation();
    const testCall = wrapUrqlMutation([testState, testMutation]);

    const [removeState, removeMutation] = useRemoveLeadConnectAddOnMutation();
    const remove = wrapUrqlMutation([removeState, removeMutation], {
        onSuccessMessage: `${LEAD_CONNECT_NAME} cancelled. Your number is kept for 30 days.`,
    });

    const [disconnectState, disconnectMutation] = useDisconnectIntegrationMutation();
    const disconnect = wrapUrqlMutation([disconnectState, disconnectMutation]);

    const app = status.data?.app;
    const integration = app?.leadconnect;
    const state = getLeadConnectCardState(app?.addOns?.leadConnect?.status, !!integration?.isLinked);
    // An admin grant is not billed, so there is nothing for the customer to cancel.
    const granted = app?.addOns?.leadConnect?.source === "admin";

    // Once set up, the form is the saved setup; only a first setup starts from the location.
    const settings = integration?.leadConnectSettings;
    // Memoized on the query's data: the dialog restarts its form whenever the defaults change.
    const defaults = useMemo(
        () => (settings ? leadConnectFormFromSettings(settings, app || {}) : defaultLeadConnectForm(app || {})),
        [settings, app]
    );

    const refresh = useCallback(() => {
        reexecuteStatus({ requestPolicy: "network-only" });
        if (typeof onChanged === "function") {
            onChanged();
        }
    }, [reexecuteStatus, onChanged]);

    const handlePurchase = useCallback(async () => {
        // Checkout comes back to the integrations page either way.
        const returnPath = `/${organizationId}/${appId}/settings/integrations`;
        const response = await purchase({ appId, successPath: returnPath, cancelPath: returnPath });
        const result = response?.data?.app?.update?.subscriptions?.addLeadConnect;
        if (result?.result === "CHECKOUT" && result.checkoutUrl) {
            window.location.assign(result.checkoutUrl);
        } else if (result?.result === "ADDED") {
            dispatch(addNotification(`${LEAD_CONNECT_NAME} added. Setup opens once the payment confirms.`));
            refresh();
        }
    }, [appId, organizationId, purchase, dispatch, refresh]);

    const handleSetup = useCallback(
        async (variables: Omit<AddLeadConnectIntegrationMutationVariables, "appId">) => {
            const response = await setup({ appId, ...variables });
            if (!response?.error) {
                refresh();
            }
        },
        [appId, setup, refresh]
    );

    const handleTestCall = useCallback(async () => {
        const response = await testCall({ appId });
        const result = response?.data?.app?.update?.integration?.leadConnectTestCall;
        if (result) {
            const { message, isError } = leadConnectTestCallMessage(result);
            dispatch(addNotification(message, isError));
        }
    }, [appId, testCall, dispatch]);

    // Cancelling also removes the setup, so the integrations page no longer shows it connected.
    // Buying again and saving a setup within 30 days gets the same number back.
    const handleCancelAddOn = useCallback(async () => {
        // A failure on either step has already been toasted by wrapUrqlMutation.
        const response = await remove({ appId });
        if (!response?.error) {
            await disconnect({ appId, type: IntegrationType.Leadconnect });
            refresh();
        }
        // Opened from the card, only the confirm was showing: close either way so the card can retry.
        if (confirmCancelOnOpen) {
            onClose();
        }
    }, [appId, remove, disconnect, refresh, confirmCancelOnOpen, onClose]);

    return (
        <LeadConnectDialog
            active={active}
            state={state}
            loading={!status.data && status.fetching}
            defaults={defaults}
            defaultsAreSaved={!!settings}
            confirmCancelOnOpen={confirmCancelOnOpen}
            number={integration?.leadConnectNumber}
            purchasing={purchaseState?.fetching}
            saving={setupState?.fetching}
            testing={testState?.fetching}
            cancelling={removeState?.fetching || disconnectState?.fetching}
            onClose={onClose}
            onPurchase={handlePurchase}
            onSetup={handleSetup}
            onTestCall={handleTestCall}
            onCancelAddOn={granted ? undefined : handleCancelAddOn}
        />
    );
};
